/**
 * Patients List Page
 * Main page for viewing and managing patients
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Grid,
  List,
  Filter,
  UserPlus,
  Users,
} from 'lucide-react';
import { usePatientsQuery, PatientFilters } from '../hooks/usePatients';
import { PatientCard } from '../components/patients/PatientCard';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { formatKennitala, formatDate, debounce } from '../lib/utils';

export function PatientsPage() {
  const navigate = useNavigate();
  const { provider } = useAuth();

  // View state
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [showFilters, setShowFilters] = useState(false);

  // Filter state
  const [filters, setFilters] = useState<PatientFilters>({
    search: '',
    sort_by: 'name',
    sort_order: 'asc',
    limit: 20,
    offset: 0,
  });

  // Fetch patients
  const { data, isLoading, error } = usePatientsQuery(filters);

  // Debounced search
  const handleSearch = useMemo(
    () =>
      debounce((value: string) => {
        setFilters((prev) => ({ ...prev, search: value, offset: 0 }));
      }, 300),
    []
  );

  const handleFilterChange = (key: keyof PatientFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value, offset: 0 }));
  };

  const handlePageChange = (newOffset: number) => {
    setFilters((prev) => ({ ...prev, offset: newOffset }));
  };

  const patients = data?.data || [];
  const meta = data?.meta;
  const totalPages = meta ? Math.ceil((meta.total || 0) / (meta.limit || 1)) : 0;
  const currentPage = meta ? meta.page : 1;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sjúklingar</h1>
          <p className="mt-1 text-gray-600">
            Stjórna sjúklingaskrá og heilsufarsupplýsingum
          </p>
        </div>

        {provider?.permissions.edit && (
          <button
            onClick={() => navigate('/patients/new')}
            className="flex items-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nýr sjúklingur
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Allir sjúklingar</p>
              <p className="text-2xl font-bold text-gray-900">{meta?.total || 0}</p>
            </div>
            <div className="w-12 h-12 bg-brand-primary/10 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-brand-primary" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Virkir sjúklingar</p>
              <p className="text-2xl font-bold text-gray-900">
                {patients.filter((p) => p.is_active).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
              <UserPlus className="w-6 h-6 text-success" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-soft p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">MyHealth tengdir</p>
              <p className="text-2xl font-bold text-gray-900">
                {patients.filter((p) => p.myhealth_user_id).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-info/10 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-info" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Leita að nafni, kennitölu, síma eða netfangi..."
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>

          {/* View Toggle */}
          <div className="flex gap-2 border border-gray-300 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${
                viewMode === 'grid'
                  ? 'bg-brand-primary text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded ${
                viewMode === 'table'
                  ? 'bg-brand-primary text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 border rounded-lg flex items-center gap-2 transition-colors ${
              showFilters
                ? 'border-brand-primary text-brand-primary bg-brand-primary/5'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-5 h-5" />
            Síur
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                MyHealth staða
              </label>
              <select
                value={
                  filters.has_myhealth === undefined
                    ? 'all'
                    : filters.has_myhealth
                    ? 'linked'
                    : 'unlinked'
                }
                onChange={(e) =>
                  handleFilterChange(
                    'has_myhealth',
                    e.target.value === 'all'
                      ? undefined
                      : e.target.value === 'linked'
                  )
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              >
                <option value="all">Allir</option>
                <option value="linked">Tengdir</option>
                <option value="unlinked">Ótengdir</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Staða
              </label>
              <select
                value={filters.status || 'active'}
                onChange={(e) =>
                  handleFilterChange('status', e.target.value as any)
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              >
                <option value="active">Virkir</option>
                <option value="inactive">Óvirkir</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Raða eftir
              </label>
              <select
                value={filters.sort_by}
                onChange={(e) =>
                  handleFilterChange('sort_by', e.target.value as any)
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
              >
                <option value="name">Nafni</option>
                <option value="created_at">Stofnað</option>
                <option value="last_visit">Síðustu heimsókn</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner text="Hleð sjúklingum..." size="lg" />
        </div>
      ) : error ? (
        <div className="bg-error/10 border border-error/20 rounded-lg p-6 text-center">
          <p className="text-error font-medium">Villa við að sækja sjúklinga</p>
          <p className="text-sm text-gray-600 mt-1">Vinsamlegast reyndu aftur</p>
        </div>
      ) : patients.length === 0 ? (
        <div className="bg-white rounded-xl shadow-soft p-12 text-center">
          <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Engir sjúklingar fundust
          </h3>
          <p className="text-gray-600 mb-6">
            {filters.search
              ? 'Engin niðurstaða fannst fyrir leitina þína'
              : 'Byrjaðu með að búa til nýjan sjúkling'}
          </p>
          {provider?.permissions.edit && !filters.search && (
            <button
              onClick={() => navigate('/patients/new')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors"
            >
              <Plus className="w-5 h-5" />
              Búa til sjúkling
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Grid View */}
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {patients.map((patient) => (
                <PatientCard key={patient.id} patient={patient} />
              ))}
            </div>
          ) : (
            /* Table View */
            <div className="bg-white rounded-xl shadow-soft overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Nafn
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Kennitala
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Símanúmer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Síðasta heimsókn
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      MyHealth
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {patients.map((patient) => (
                    <tr
                      key={patient.id}
                      onClick={() => navigate(`/patients/${patient.id}`)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900">
                          {patient.first_name} {patient.last_name}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-mono text-gray-600">
                          {patient.kennitala && formatKennitala(patient.kennitala!)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {patient.phone}
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {patient.last_visit_date
                          ? formatDate(patient.last_visit_date!)
                          : '—'}
                      </td>
                      <td className="px-6 py-4">
                        {patient.myhealth_user_id ? (
                          <span className="px-2 py-1 bg-success/10 text-success text-xs font-medium rounded-full">
                            Tengdur
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                            Ótengdur
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between bg-white rounded-xl shadow-soft p-4">
              <div className="text-sm text-gray-600">
                Síða {currentPage} af {totalPages} ({meta?.total} sjúklingar)
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handlePageChange(Math.max(0, filters.offset! - filters.limit!))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Fyrri
                </button>
                <button
                  onClick={() => handlePageChange(filters.offset! + filters.limit!)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Næsta
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
